import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  TrendingUp, TrendingDown, ShieldCheck, Globe2, Zap, Activity, ArrowUpRight,
  Sparkles, CheckCircle2, ChevronRight, BarChart3, Wallet, Crown,
  CreditCard, Boxes, Landmark, Rocket,
} from "lucide-react";
import Navbar from "@/components/marketing/Navbar";
import Footer from "@/components/marketing/Footer";
import Sparkline from "@/components/market/Sparkline";
import CoinDetailModal from "@/components/market/CoinDetailModal";
import api from "@/lib/api";
import { money, num } from "@/lib/format";

const IMG = {
  hero: "https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwxfHxmb3JleCUyMHN0b2NrJTIwbWFya2V0JTIwdHJhZGluZyUyMHNjcmVlbiUyMGRhcmt8ZW58MHx8fHwxNzg2NTk4OTIxfDA&ixlib=rb-4.1.0&q=85",
  gold: "https://images.unsplash.com/photo-1636891925388-216a9182ab5b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjd8MHwxfHNlYXJjaHwyfHxnb2xkJTIwYmFycyUyMGx1eHVyeSUyMHdlYWx0aCUyMGRhcmt8ZW58MHx8fGJsYWNrfDE3ODY1OTkwNTl8MA&ixlib=rb-4.1.0&q=85",
};

const fade = { initial: { opacity: 0, y: 30 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true }, transition: { duration: 0.6 } };
const Overline = ({ children }) => (<span className="text-xs font-semibold uppercase tracking-[0.25em] text-amber-400">{children}</span>);
const pct = (v) => `${v >= 0 ? "+" : ""}${Number(v || 0).toFixed(2)}%`;
const fmtPrice = (v) => "$" + Number(v || 0).toLocaleString("en-US", { maximumFractionDigits: v < 10 ? 4 : 2 });

function Stat({ value, label }) {
  return (
    <div className="text-center">
      <div className="font-display font-extrabold text-2xl sm:text-3xl gold-text font-mono-fin">{value}</div>
      <div className="text-xs text-slate-400 mt-1 uppercase tracking-wide">{label}</div>
    </div>
  );
}

// Live simulated trade tape derived from crypto data
function TradeTape({ coins }) {
  const [trades, setTrades] = useState([]);
  const ref = useRef(coins);
  ref.current = coins;

  useEffect(() => {
    const tick = () => {
      const list = ref.current;
      if (!list || !list.length) return;
      const c = list[Math.floor(Math.random() * Math.min(list.length, 8))];
      const side = Math.random() > 0.5 ? "BUY" : "SELL";
      const amt = (Math.random() * 4 + 0.05);
      setTrades((prev) => [{
        id: Date.now() + Math.random(), sym: c.symbol, side,
        price: c.price, amt: amt.toFixed(3), total: amt * c.price,
        time: new Date().toLocaleTimeString("en-US", { hour12: false }),
      }, ...prev].slice(0, 9));
    };
    const iv = setInterval(tick, 1400);
    return () => clearInterval(iv);
  }, []);

  return (
    <div className="glass rounded-2xl p-5" data-testid="trade-tape">
      <div className="flex items-center gap-2 mb-4"><Activity className="w-4 h-4 text-emerald-400" /><span className="font-display font-semibold text-sm">Live Trade Tape</span><span className="ml-auto flex items-center gap-1.5 text-xs text-emerald-400"><span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" /> LIVE</span></div>
      <div className="space-y-1.5 min-h-[240px]">
        {trades.length === 0 && <div className="text-slate-600 text-xs">Streaming market trades...</div>}
        {trades.map((t) => (
          <div key={t.id} className="flex items-center justify-between text-xs font-mono-fin py-1.5 border-b border-slate-800/50">
            <span className={`px-1.5 py-0.5 rounded font-bold ${t.side === "BUY" ? "text-emerald-400 bg-emerald-500/10" : "text-red-400 bg-red-500/10"}`}>{t.side}</span>
            <span className="text-slate-200 font-semibold w-14">{t.sym}</span>
            <span className="text-slate-400 w-16 text-right">{t.amt}</span>
            <span className="text-amber-300 w-24 text-right">{fmtPrice(t.price)}</span>
            <span className="text-slate-600 w-16 text-right">{t.time}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Landing() {
  const [crypto, setCrypto] = useState([]);
  const [forex, setForex] = useState([]);
  const [selectedCoin, setSelectedCoin] = useState(null);

  useEffect(() => {
    const loadCrypto = () => api.get("/market/crypto").then(({ data }) => setCrypto(data)).catch(() => {});
    const loadForex = () => api.get("/market/forex").then(({ data }) => setForex(data)).catch(() => {});
    loadCrypto(); loadForex();
    const ic = setInterval(loadCrypto, 60000);
    const iff = setInterval(loadForex, 30000);
    return () => { clearInterval(ic); clearInterval(iff); };
  }, []);

  const gainers = [...crypto].sort((a, b) => (b.change_24h || 0) - (a.change_24h || 0)).slice(0, 3);
  const losers = [...crypto].sort((a, b) => (a.change_24h || 0) - (b.change_24h || 0)).slice(0, 3);

  return (
    <div className="bg-[#050507] text-slate-100 overflow-x-hidden">
      <Navbar />

      {/* HERO */}
      <section id="home" className="relative min-h-screen flex items-center pt-24">
        <div className="absolute inset-0">
          <img src={IMG.hero} alt="Trading markets" className="w-full h-full object-cover opacity-20" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#050507]/70 via-[#050507]/85 to-[#050507]" />
        </div>
        <div className="grid-overlay absolute inset-0 opacity-40" />
        <div className="gold-glow -top-20 left-1/3" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 w-full grid lg:grid-cols-2 gap-12 items-center">
          <motion.div {...fade}>
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full glass mb-6">
              <Sparkles className="w-4 h-4 text-amber-400" />
              <span className="text-xs uppercase tracking-[0.2em] text-amber-300">Trusted Global Wealth Platform</span>
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-none">
              Trade the world's <br /> <span className="gold-text">Crypto & Forex</span> markets
            </h1>
            <p className="mt-6 text-slate-300 text-lg max-w-xl leading-relaxed">
              Real-time crypto prices, live forex pairs, market trends and trades — all in one premium platform. Smart choice. Brighter future.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link to="/register" data-testid="hero-start-btn" className="gold-btn px-8 py-4 rounded-xl font-bold flex items-center gap-2">
                Start Trading <ArrowUpRight className="w-5 h-5" />
              </Link>
              <a href="#crypto" className="px-8 py-4 rounded-xl border border-amber-500/30 hover:border-amber-400 hover:bg-amber-500/10 transition-all font-semibold text-amber-200">
                View Markets
              </a>
            </div>
            <div className="mt-12 grid grid-cols-4 gap-4 max-w-lg">
              <Stat value="50+" label="Countries" />
              <Stat value="$7.5T" label="Forex/Day" />
              <Stat value="10+" label="Years Exp" />
              <Stat value="24/7" label="Markets" />
            </div>
          </motion.div>
          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7 }} className="space-y-4">
            <div className="glass rounded-2xl p-5" data-testid="hero-ticker">
              <div className="flex items-center justify-between mb-4">
                <span className="font-display font-semibold text-sm flex items-center gap-2"><BarChart3 className="w-4 h-4 text-amber-400" /> Top Markets</span>
                <span className="flex items-center gap-1.5 text-xs text-emerald-400"><span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" /> LIVE</span>
              </div>
              <div className="space-y-3">
                {crypto.slice(0, 5).map((c) => {
                  const up = (c.change_24h || 0) >= 0;
                  return (
                    <div key={c.id} onClick={() => setSelectedCoin(c)} data-testid={`ticker-${c.symbol}`} className="flex items-center gap-3 cursor-pointer hover:bg-amber-500/5 -mx-2 px-2 py-1 rounded-lg transition-colors">
                      <img src={c.image} alt={c.symbol} className="w-7 h-7 rounded-full" />
                      <div className="flex-1 min-w-0"><div className="text-sm font-semibold">{c.symbol}</div></div>
                      <Sparkline data={c.sparkline} up={up} width={70} height={26} />
                      <div className="text-right w-24"><div className="font-mono-fin text-sm">{fmtPrice(c.price)}</div><div className={`font-mono-fin text-xs ${up ? "text-emerald-400" : "text-red-400"}`}>{pct(c.change_24h)}</div></div>
                    </div>
                  );
                })}
                {crypto.length === 0 && <div className="text-slate-600 text-xs py-8 text-center">Loading live prices...</div>}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* TRUST BAR */}
      <section className="border-y border-amber-500/15 bg-[#0A0A0E] py-8">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-2 md:grid-cols-4 gap-8">
          {[
            { icon: Globe2, t: "Global Access", s: "Trade from anywhere" },
            { icon: ShieldCheck, t: "Secure Blockchain", s: "USDT BEP20 network" },
            { icon: Zap, t: "Fast Execution", s: "Real-time market data" },
            { icon: Activity, t: "24/7 Markets", s: "Crypto never sleeps" },
          ].map((x) => (
            <div key={x.t} className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-xl bg-amber-500/10 border border-amber-500/20 grid place-items-center shrink-0"><x.icon className="w-5 h-5 text-amber-400" /></div>
              <div><div className="font-semibold text-sm">{x.t}</div><div className="text-xs text-slate-500">{x.s}</div></div>
            </div>
          ))}
        </div>
      </section>

      {/* CRYPTO MARKET */}
      <section id="crypto" className="py-24 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <motion.div {...fade} className="max-w-2xl">
            <Overline>Live Cryptocurrency Market</Overline>
            <h2 className="font-display text-3xl sm:text-4xl font-bold mt-3">Real-time <span className="gold-text">crypto prices</span> & trends</h2>
            <p className="text-slate-400 mt-4">Powered by live market data. Prices refresh automatically every minute.</p>
          </motion.div>
          <motion.div {...fade} className="glass rounded-2xl mt-10 overflow-hidden" data-testid="crypto-market-table">
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[720px]">
                <thead><tr className="text-amber-400 uppercase text-xs tracking-wider border-b border-amber-500/20">
                  <th className="text-left py-4 px-5">#</th><th className="text-left py-4 px-5">Asset</th>
                  <th className="text-right py-4 px-5">Price</th><th className="text-right py-4 px-5">24h</th>
                  <th className="text-center py-4 px-5">7d Trend</th><th className="text-right py-4 px-5">Market Cap</th><th className="text-right py-4 px-5">Volume 24h</th>
                </tr></thead>
                <tbody>
                  {crypto.map((c, i) => {
                    const up = (c.change_24h || 0) >= 0;
                    return (
                      <tr key={c.id} onClick={() => setSelectedCoin(c)} className="border-b border-slate-800/50 hover:bg-amber-500/5 cursor-pointer" data-testid={`crypto-row-${c.symbol}`}>
                        <td className="py-3 px-5 text-slate-500 font-mono-fin">{i + 1}</td>
                        <td className="py-3 px-5"><div className="flex items-center gap-3"><img src={c.image} alt={c.symbol} className="w-8 h-8 rounded-full" /><div><div className="font-semibold">{c.name}</div><div className="text-xs text-slate-500">{c.symbol}</div></div></div></td>
                        <td className="py-3 px-5 text-right font-mono-fin font-semibold">{fmtPrice(c.price)}</td>
                        <td className={`py-3 px-5 text-right font-mono-fin font-bold ${up ? "text-emerald-400" : "text-red-400"}`}><span className="inline-flex items-center gap-1">{up ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />}{pct(c.change_24h)}</span></td>
                        <td className="py-3 px-5"><div className="flex justify-center"><Sparkline data={c.sparkline} up={up} width={110} height={34} /></div></td>
                        <td className="py-3 px-5 text-right font-mono-fin text-slate-300">{money(c.market_cap, 0)}</td>
                        <td className="py-3 px-5 text-right font-mono-fin text-slate-400">{money(c.volume, 0)}</td>
                      </tr>
                    );
                  })}
                  {crypto.length === 0 && <tr><td colSpan={7} className="py-10 text-center text-slate-500">Loading live market data...</td></tr>}
                </tbody>
              </table>
            </div>
          </motion.div>
        </div>
      </section>

      {/* FOREX MARKET */}
      <section id="forex" className="py-24 bg-[#08080C] border-y border-amber-500/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <motion.div {...fade} className="max-w-2xl">
            <Overline>Forex Market</Overline>
            <h2 className="font-display text-3xl sm:text-4xl font-bold mt-3">Major <span className="gold-text">currency pairs</span>, live</h2>
            <p className="text-slate-400 mt-4">The world's largest market — $7.5T daily volume. Live bid/ask and intraday trends.</p>
          </motion.div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-10">
            {forex.map((f) => {
              const up = (f.change_24h || 0) >= 0;
              return (
                <motion.div key={f.pair} {...fade} className="rounded-2xl bg-[#0D0E12] border border-amber-500/20 p-5 hover:border-amber-500/40 transition-all" data-testid={`forex-card-${f.pair.replace('/','')}`}>
                  <div className="flex items-center justify-between">
                    <span className="font-display font-bold">{f.pair}</span>
                    <span className={`font-mono-fin text-xs font-bold px-2 py-0.5 rounded ${up ? "text-emerald-400 bg-emerald-500/10" : "text-red-400 bg-red-500/10"}`}>{pct(f.change_24h)}</span>
                  </div>
                  <div className="font-mono-fin text-2xl font-extrabold mt-3">{f.price}</div>
                  <div className="mt-2"><Sparkline data={f.sparkline} up={up} width={220} height={40} /></div>
                  <div className="flex justify-between mt-3 text-xs font-mono-fin text-slate-500"><span>Bid {f.bid}</span><span>Ask {f.ask}</span></div>
                </motion.div>
              );
            })}
            {forex.length === 0 && <div className="col-span-full text-center text-slate-500 py-10">Loading forex data...</div>}
          </div>
        </div>
      </section>

      {/* TRENDS & TRADES */}
      <section id="trends" className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <motion.div {...fade} className="max-w-2xl">
            <Overline>Market Trends & Trades</Overline>
            <h2 className="font-display text-3xl sm:text-4xl font-bold mt-3">Spot the <span className="gold-text">movers</span> in real time</h2>
          </motion.div>
          <div className="grid lg:grid-cols-3 gap-6 mt-10">
            <motion.div {...fade} className="glass rounded-2xl p-6">
              <div className="flex items-center gap-2 mb-4"><TrendingUp className="w-5 h-5 text-emerald-400" /><span className="font-display font-semibold">Top Gainers (24h)</span></div>
              <div className="space-y-3">
                {gainers.map((c) => (
                  <div key={c.id} className="flex items-center gap-3" data-testid={`gainer-${c.symbol}`}>
                    <img src={c.image} alt={c.symbol} className="w-8 h-8 rounded-full" />
                    <div className="flex-1"><div className="font-semibold text-sm">{c.symbol}</div><div className="text-xs text-slate-500">{fmtPrice(c.price)}</div></div>
                    <span className="font-mono-fin text-emerald-400 font-bold text-sm">{pct(c.change_24h)}</span>
                  </div>
                ))}
                {gainers.length === 0 && <div className="text-slate-600 text-xs">Loading...</div>}
              </div>
            </motion.div>
            <motion.div {...fade} className="glass rounded-2xl p-6">
              <div className="flex items-center gap-2 mb-4"><TrendingDown className="w-5 h-5 text-red-400" /><span className="font-display font-semibold">Top Losers (24h)</span></div>
              <div className="space-y-3">
                {losers.map((c) => (
                  <div key={c.id} className="flex items-center gap-3" data-testid={`loser-${c.symbol}`}>
                    <img src={c.image} alt={c.symbol} className="w-8 h-8 rounded-full" />
                    <div className="flex-1"><div className="font-semibold text-sm">{c.symbol}</div><div className="text-xs text-slate-500">{fmtPrice(c.price)}</div></div>
                    <span className="font-mono-fin text-red-400 font-bold text-sm">{pct(c.change_24h)}</span>
                  </div>
                ))}
                {losers.length === 0 && <div className="text-slate-600 text-xs">Loading...</div>}
              </div>
            </motion.div>
            <motion.div {...fade}><TradeTape coins={crypto} /></motion.div>
          </div>
        </div>
      </section>

      {/* WHO WE ARE */}
      <section id="about" className="py-24 bg-[#08080C] border-y border-amber-500/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 grid lg:grid-cols-2 gap-14 items-center">
          <motion.div {...fade} className="relative rounded-2xl overflow-hidden border border-amber-500/20">
            <img src={IMG.gold} alt="Wealth" className="w-full h-[420px] object-cover" />
            <div className="absolute inset-0 bg-gradient-to-tr from-black/60 to-transparent" />
          </motion.div>
          <motion.div {...fade}>
            <Overline>Who We Are</Overline>
            <h2 className="font-display text-3xl sm:text-4xl font-bold mt-3">A decade of <span className="gold-text">market expertise</span></h2>
            <p className="text-slate-400 mt-4">With 10+ years across 50+ countries, our team blends advanced trading technology, rigorous risk management, and capital protection to deliver consistent results.</p>
            <div className="grid sm:grid-cols-2 gap-4 mt-8">
              {["Experienced Professionals", "Advanced Technology", "Risk Management", "Capital Protection", "AI Trading Tools", "24/7 Global Support"].map((x) => (
                <div key={x} className="flex items-center gap-3"><ShieldCheck className="w-5 h-5 text-amber-400 shrink-0" /><span className="text-sm">{x}</span></div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* PAYMENTS */}
      <section className="py-24">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-12">
          <motion.div {...fade} className="glass rounded-2xl p-8 grid md:grid-cols-2 gap-8 items-center">
            <div>
              <Wallet className="w-8 h-8 text-amber-400 mb-4" />
              <h3 className="font-display text-2xl font-bold">Fast & Secure Payments</h3>
              <p className="text-slate-400 mt-3 text-sm">Powered by USDT BEP20 on Binance Smart Chain for instant, low-fee, borderless deposits and withdrawals.</p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {["Fast Deposits", "24/7 Processing", "Low Fees", "Secure Blockchain"].map((x) => (
                <div key={x} className="flex items-center gap-2 text-sm"><CheckCircle2 className="w-4 h-4 text-emerald-400" />{x}</div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 relative overflow-hidden">
        <div className="gold-glow top-0 left-1/2 -translate-x-1/2" />
        <motion.div {...fade} className="max-w-4xl mx-auto px-6 text-center relative">
          <Crown className="w-10 h-10 text-amber-400 mx-auto mb-4" />
          <h2 className="font-display text-4xl sm:text-5xl font-extrabold">Your <span className="gold-text">brighter future</span> starts today</h2>
          <p className="text-slate-400 mt-5 text-lg">Join thousands of traders building wealth across global markets with MoneyMoonX.</p>
          <Link to="/register" data-testid="cta-register-btn" className="inline-flex items-center gap-2 gold-btn px-10 py-4 rounded-xl font-bold mt-8 text-lg">
            Create Your Account <ChevronRight className="w-5 h-5" />
          </Link>
        </motion.div>
      </section>

      <section id="roadmap" className="py-24 bg-[#08080C] border-y border-amber-500/10 relative overflow-hidden">
        <div className="gold-glow top-1/4 right-0" />
        <div className="max-w-6xl mx-auto px-6 relative">
          <motion.div {...fade} className="text-center">
            <span className="text-amber-400 text-sm font-semibold uppercase tracking-[0.2em]">Our Vision</span>
            <h2 className="font-display text-4xl sm:text-5xl font-extrabold mt-3">Global Expansion <span className="gold-text">Roadmap</span></h2>
            <p className="text-slate-400 mt-4 max-w-2xl mx-auto">A clear path to a full-scale global financial ecosystem — from our own coin to worldwide reach.</p>
          </motion.div>

          <div className="relative mt-16">
            <div className="hidden lg:block absolute top-[4.5rem] left-0 right-0 h-px bg-gradient-to-r from-transparent via-amber-500/40 to-transparent" />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { year: "2027", icon: CreditCard, title: "Launch Coin & Crypto Payment Card" },
                { year: "2028", icon: Boxes, title: "Blockchain Technology" },
                { year: "2029", icon: Landmark, title: "Broker House" },
                { year: "2030+", icon: Rocket, title: "Global Expansion" },
              ].map((m, i) => (
                <motion.div key={m.year} {...fade} transition={{ delay: i * 0.1 }}
                  data-testid={`roadmap-${m.year.replace('+', 'plus')}`}
                  className="relative rounded-2xl bg-[#0D0E12] border border-amber-500/25 p-6 text-center hover:border-amber-500/60 hover:-translate-y-1 transition-all group">
                  <div className="font-display text-3xl font-extrabold gold-text">{m.year}</div>
                  <div className="w-16 h-16 rounded-2xl bg-amber-500/10 border border-amber-500/30 grid place-items-center mx-auto my-5 group-hover:bg-amber-500/20 transition-all">
                    <m.icon className="w-8 h-8 text-amber-400" />
                  </div>
                  <div className="text-sm font-semibold uppercase tracking-wide text-slate-200">{m.title}</div>
                  <div className="hidden lg:block absolute -top-[2.35rem] left-1/2 -translate-x-1/2 w-3 h-3 rounded-full bg-amber-400 ring-4 ring-amber-500/20" />
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <Footer />
      {selectedCoin && <CoinDetailModal coin={selectedCoin} onClose={() => setSelectedCoin(null)} />}
    </div>
  );
}
